'use client';

import { useEffect, useState } from 'react';

interface Section {
  id: string;
  title: string;
}

interface TableOfContentsProps {
  sections: Section[];
  title?: string;
}

export default function TableOfContents({
  sections,
  title = 'On this page',
}: TableOfContentsProps) {
  // Default to first section on load
  const [activeSection, setActiveSection] = useState<string>(
    sections.length > 0 ? sections[0].id : ''
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-80px 0px -80% 0px',
        threshold: 0,
      }
    );

    // Observe all section headings
    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [sections]);

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const disclosure = e.currentTarget.closest('details');
      if (disclosure) disclosure.open = false;
      window.history.replaceState(
        window.history.state,
        '',
        `#${encodeURIComponent(sectionId)}`
      );
      const offset = 100; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <nav className="sticky top-24 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:overscroll-contain">
      {title && (
        <h4
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: 'var(--color-foreground-secondary)' }}
        >
          {title}
        </h4>
      )}
      <ul className="space-y-2">
        {sections.map(section => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              onClick={e => handleClick(e, section.id)}
              className={`block text-sm py-1 border-l-2 pl-3 transition-colors ${
                activeSection === section.id
                  ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 font-medium'
                  : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              style={{
                color:
                  activeSection === section.id
                    ? undefined
                    : 'var(--color-foreground-secondary)',
              }}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
